import { useState, type ReactNode } from 'react';

export interface DisclosureProps {
  /** Always-visible summary row content (the click target). */
  summary: ReactNode;
  /** Initial open state when uncontrolled. */
  defaultOpen?: boolean;
  /** Controlled open state. When set, `onToggle` owns updates. */
  open?: boolean;
  /** Fires with the next open state on every toggle. */
  onToggle?: (open: boolean) => void;
  /** Body content — rendered only while open. */
  children: ReactNode;
  className?: string;
  /** Test id — the toggle button gets `${dataTestId}-toggle`. */
  dataTestId?: string;
}

/**
 * Expandable disclosure section — a summary row that toggles a content region.
 * Used for the Version-history section of the function-roles editor; reusable
 * for any "click to reveal" settings sub-section. Works controlled (`open` +
 * `onToggle`) or uncontrolled (`defaultOpen`). CSS namespace `pn-disclosure`.
 */
export function Disclosure({
  summary,
  defaultOpen = false,
  open,
  onToggle,
  children,
  className,
  dataTestId
}: DisclosureProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) setInternalOpen(next);
    onToggle?.(next);
  };

  const cls = ['pn-disclosure', className].filter(Boolean).join(' ');

  return (
    <div
      className={cls}
      data-open={isOpen ? 'true' : 'false'}
      data-testid={dataTestId}
    >
      <button
        type="button"
        className="pn-disclosure-summary"
        aria-expanded={isOpen}
        onClick={toggle}
        data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
      >
        <span className="pn-disclosure-caret" aria-hidden>
          ›
        </span>
        <span className="pn-disclosure-summary-text">{summary}</span>
      </button>
      {isOpen ? (
        <div className="pn-disclosure-body">{children}</div>
      ) : null}
    </div>
  );
}
